import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Bell, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";

export default function NotificationBell() {
  const { t } = useLang();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const loadCount = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.count || 0);
    } catch { /* ignore */ }
  };

  const loadAll = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => {
    loadCount();
    const iv = setInterval(loadCount, 30000);
    const onRefresh = () => loadCount();
    window.addEventListener("obra:notif-refresh", onRefresh);
    return () => {
      clearInterval(iv);
      window.removeEventListener("obra:notif-refresh", onRefresh);
    };
  }, []);

  useEffect(() => { if (open) loadAll(); }, [open]);

  const goToSnag = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
      loadCount();
    }
    setOpen(false);
    nav(`/app/projects/${n.project_id}?tab=snags&snag=${n.snag_id || ""}`);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      await loadAll();
      loadCount();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative p-2 rounded-md hover:bg-neutral-100 transition-colors duration-200 text-neutral-700"
          aria-label="Notifications">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#B8933A] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-panel">
        <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <p className="label-eyebrow">{t("notifications")}</p>
            <p className="font-[Fraunces] font-semibold text-neutral-950 mt-0.5">
              {unread > 0 ? `${unread} ${t("unread")}` : t("all_caught_up")}
            </p>
          </div>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAll} className="text-xs" data-testid="mark-all-read-button">
              <Check className="w-3.5 h-3.5 mr-1" />{t("mark_all_read")}
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="p-6 text-center text-sm text-neutral-400">{t("no_notifications")}</p>
          ) : items.map((n) => (
            <button
              key={n.id}
              onClick={() => goToSnag(n)}
              data-testid={`notification-item-${n.id}`}
              className={`w-full text-left p-4 border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 transition-colors duration-200 ${!n.read ? "bg-[#F5F1E6]/40" : ""}`}>
              <div className="flex items-start gap-3">
                <div className="mt-0.5 w-7 h-7 rounded-md bg-red-100 text-red-700 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-neutral-950 truncate">{n.title}</p>
                  <p className="text-xs text-neutral-500 truncate">{n.body}</p>
                  <p className="text-[10px] text-neutral-400 mt-1">
                    {new Date(n.created_at).toLocaleString()}
                  </p>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-[#B8933A] flex-shrink-0 mt-2" />}
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
